import { useMemo } from 'react'
import { capitalize } from 'lodash'
import { Download, FileText } from 'lucide-react'
import { DonationReportFilters } from './DonationReportFilters'
import type { reportFilterSchema } from '@/schemas/filterSchemas'
import { Badge } from '@/components/custom/badge'
import { TBody, TD, TH, THead, TR, Table } from '@/components/custom/table'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useChurchesStore } from '@/features/churches/store/churchesStore'
import { useMembersStore } from '@/features/members/store/membersStore'
import { useDonationsStore } from '@/features/donations/store/donationsStore'
import { usePermissions } from '@/utils/permissions'
import { formatCurrency } from '@/utils/currencyUtils'
import { formatDate, parseLocalDateString } from '@/utils/timeZoneUtils'
import { useCleanQueryStates, useIsMobile, usePagination } from '@/hooks'
import { PaginationWrapper } from '@/components/custom/PaginationWrapper'

export type DonationReportFilters = {
  churchId: string
  memberId: string
  startDate?: string
  endDate?: string
  status: 'all' | 'verified' | 'pending'
}

export const initialFilter: DonationReportFilters = {
  churchId: 'all',
  memberId: 'all',
  startDate: '',
  endDate: '',
  status: 'all',
}

type DonationReportCardProps = {
  mode?: 'donations' | 'registrations'
}

export default function DonationReportCard({
  mode = 'donations',
}: DonationReportCardProps) {
  const isRegistrationMode = mode === 'registrations'
  const isMobile = useIsMobile()
  const permissions = usePermissions()

  const churches = useChurchesStore((s) => s.churches)
  const members = useMembersStore((s) => s.members)
  const donations = useDonationsStore((s) => s.donations)

  const [filters, setFilters] =
    useCleanQueryStates<typeof reportFilterSchema>('/authed/reports')

  // Filter churches based on user permissions
  const assignedChurchIds = permissions.getUserAssignedChurches()
  const filteredChurches =
    assignedChurchIds.length === 0
      ? churches // Super admin sees all churches
      : churches.filter((church) => assignedChurchIds.includes(church._id))

  // Filter members based on selected church
  const churchMembers =
    filters.churchId !== 'all'
      ? members.filter((member) => member.churchId === filters.churchId)
      : members

  // Filter donations based on all criteria
  const filteredDonations = useMemo(() => {
    return donations.filter((donation) => {
      if (
        isRegistrationMode
          ? !donation.isRegistrationFee
          : donation.isRegistrationFee
      ) {
        return false
      }

      // Role-based filtering
      if (!permissions.isSystemAdmin()) {
        if (
          !donation.churchId ||
          !permissions.canAccessDonation(donation.churchId)
        ) {
          return false
        }
      }

      // Church filter
      if (filters.churchId !== '' && donation.churchId !== filters.churchId) {
        return false
      }

      // Member filter
      if (filters.memberId !== '' && donation.donorId !== filters.memberId) {
        return false
      }

      // Date range filter - use parseLocalDateString for consistent comparison
      const donationDate = new Date(donation.date)
      const startDate = filters.startDate
        ? parseLocalDateString(filters.startDate)
        : null
      const endDate = filters.endDate
        ? parseLocalDateString(filters.endDate)
        : null

      if (startDate && donationDate < startDate) {
        return false
      }
      if (endDate && donationDate > endDate) {
        return false
      }

      // Status filter
      if (filters.status !== '' && donation.status !== filters.status) {
        return false
      }

      return true
    })
  }, [donations, filters, permissions])

  // Calculate summary statistics
  const summary = useMemo(() => {
    const totalAmount = filteredDonations.reduce((sum, d) => sum + d.amount, 0)
    const verifiedAmount = filteredDonations
      .filter((d) => d.status === 'verified')
      .reduce((sum, d) => sum + d.amount, 0)
    const pendingAmount = filteredDonations
      .filter((d) => d.status === 'pending')
      .reduce((sum, d) => sum + d.amount, 0)

    return {
      totalDonations: filteredDonations.length,
      totalAmount,
      verifiedAmount,
      pendingAmount,
      verifiedCount: filteredDonations.filter((d) => d.status === 'verified')
        .length,
      pendingCount: filteredDonations.filter((d) => d.status === 'pending')
        .length,
    }
  }, [filteredDonations])

  const { page, pageCount, rows, setPage, goToFirstPage } = usePagination({
    data: filteredDonations,
    pageSize: 10,
  })

  const handleExport = () => {
    // Create CSV content
    const headers = [
      'Receipt #',
      'Date',
      'Church',
      'Donor',
      'Amount',
      'Method',
      'Reference #',
      'Status',
      'Notes',
    ]
    const csvContent = [
      headers.join(','),
      ...filteredDonations.map((donation) => {
        const church = churches.find((c) => c._id === donation.churchId)
        const donor = members.find((m) => m._id === donation.donorId)

        return [
          donation.receiptNumber,
          formatDate(donation.date, 'YYYY-MM-DD'),
          church ? `"${church.name}"` : 'Unassigned',
          donor ? `"${donor.name}"` : 'Unassigned',
          donation.amount / 100,
          donation.method,
          donation.donationRef || '',
          donation.status,
          donation.notes ? `"${donation.notes.replace(/"/g, '""')}"` : '',
        ].join(',')
      }),
    ].join('\n')

    // Download CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute(
      'download',
      `${isRegistrationMode ? 'registration-fee-report' : 'donation-report'}-${new Date().toISOString().split('T')[0]}.csv`,
    )
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <Card className="col-span-full">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg">
              <FileText className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <CardTitle>
                {isRegistrationMode
                  ? 'Registration Fee Report'
                  : 'Donation Report'}
              </CardTitle>
              <p className="text-sm text-gray-600">
                {isRegistrationMode
                  ? 'View and export registration fee data by church, period, and member'
                  : 'View and export donation data by church, period, and member'}
              </p>
            </div>
          </div>
          {filteredDonations.length > 0 && (
            <Button
              onClick={handleExport}
              variant="outline"
              size="sm"
              className="w-full sm:w-auto"
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Filters */}
        <DonationReportFilters
          onFilter={setFilters}
          churches={filteredChurches}
          members={churchMembers}
          goToFirstPage={goToFirstPage}
        />

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">
              {isRegistrationMode ? 'Total Registrations' : 'Total Donations'}
            </p>
            <p className="text-2xl font-semibold">{summary.totalDonations}</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">Total Amount</p>
            <p className="text-2xl font-semibold">
              {formatCurrency(summary.totalAmount)}
            </p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <p className="text-sm text-green-600">
              Verified ({summary.verifiedCount})
            </p>
            <p className="text-2xl font-semibold text-green-700">
              {formatCurrency(summary.verifiedAmount)}
            </p>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg">
            <p className="text-sm text-yellow-600">
              Pending ({summary.pendingCount})
            </p>
            <p className="text-2xl font-semibold text-yellow-700">
              {formatCurrency(summary.pendingAmount)}
            </p>
          </div>
        </div>

        {/* Donations Table */}
        {!isMobile && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {isRegistrationMode ? 'Registrations' : 'Donations'} (
                {filteredDonations.length})
              </h3>
            </div>

            <ScrollArea className="flex-1 w-full min-h-0">
              {rows.length > 0 ? (
                <div className="min-w-full">
                  <Table>
                    <THead>
                      <TR>
                        <TH className="min-w-25">Receipt #</TH>
                        <TH className="min-w-25">Date</TH>
                        <TH className="text-right min-w-25">Amount</TH>
                        <TH className="min-w-37.5">Church</TH>
                        <TH className="min-w-30">Donor</TH>
                        <TH className="min-w-20">Method</TH>
                        <TH className="min-w-25">Reference #</TH>
                        <TH className="min-w-20">Status</TH>
                        <TH className="min-w-37.5">Notes</TH>
                      </TR>
                    </THead>
                    <TBody>
                      {rows
                        .sort(
                          (a, b) =>
                            new Date(b.date).getTime() -
                            new Date(a.date).getTime(),
                        )
                        .map((donation) => {
                          const church = churches.find(
                            (c) => c._id === donation.churchId,
                          )
                          const donor = members.find(
                            (m) => m._id === donation.donorId,
                          )

                          return (
                            <TR key={donation._id}>
                              <TD>{donation.receiptNumber}</TD>
                              <TD>
                                {formatDate(donation.date, 'MMM D, YYYY')}
                              </TD>
                              <TD className="text-right">
                                {formatCurrency(donation.amount)}
                              </TD>
                              <TD>
                                {church ? church.name : 'Unassigned Church'}
                              </TD>
                              <TD>{donor ? donor.name : 'Unassigned Donor'}</TD>
                              <TD>{capitalize(donation.method)}</TD>
                              <TD>{donation.donationRef || '-'}</TD>
                              <TD>
                                <Badge
                                  color={
                                    donation.status === 'verified'
                                      ? 'green'
                                      : 'yellow'
                                  }
                                >
                                  {capitalize(donation.status)}
                                </Badge>
                              </TD>
                              <TD className="max-w-37.5 truncate">
                                {donation.notes || '-'}
                              </TD>
                            </TR>
                          )
                        })}
                    </TBody>
                  </Table>
                  <div className="hidden lg:block mt-4">
                    <PaginationWrapper
                      pageCount={pageCount}
                      currentPage={page}
                      onPageChange={setPage}
                    />
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No donations found for the selected criteria
                </div>
              )}
            </ScrollArea>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
